import React, { useState, useEffect } from 'react';
import { Box } from '@components/Box';
import { Columns } from '@components/Columns';
import { defaultBase64Placeholder } from '@components/CloudImage';
import GiftIcon from '@images/honeymoon-icons/gift_icons.svg';
import CashIcon from '@images/honeymoon-icons/icon_cash.svg';
import PlaneIcon from '@images/honeymoon-icons/icon_plane.svg';
import HeartIcon from '@images/honeymoon-icons/icon_heart.svg';
import {
  StyledUniversalRegistry,
  StyledTextPairing,
  StateIconWrapper,
  StateIconTextContainer,
  StateIcon,
  HeroTextContainer,
  IconTextPairing,
  StyledHeroTextColumn,
} from './UniversalRegistry.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { Image } from '@components/Image';
import { useTranslation } from 'react-i18next';

interface UniversalRegistryProps {}

const smallTitleProps = {
  titleVariant: 'featureHed17',
  bodyVariant: 'featureCopy17',
  titleColor: 'humanaDark6',
  alignment: 'center',
} as const;

const getHeight = (): number => {
  return (typeof window !== 'undefined' ? window.innerWidth : 0) + 600;
};

export const UniversalRegistry: React.FC<UniversalRegistryProps> = () => {
  const [resHeight, setResHeight] = useState(getHeight());
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);
  useEffect(() => {
    const onResize = () => {
      setResHeight(getHeight);
    };
    window.addEventListener('resize', onResize, false);
    return () => {
      window.removeEventListener('resize', onResize, false);
    };
  }, [setResHeight]);

  const { t } = useTranslation('honeymoonRegistry');
  const universalRegistrySectionData = t('universalRegistrySection');

  return (
    <StyledUniversalRegistry
      backgroundImage={isScrolledIntoView ? 'honeymoon-fund/all-in-one-bg.jpg' : defaultBase64Placeholder}
      marginTop={['-100px', '-100px']}
      as="section"
      id="Honeymoon_Universal_Registry"
      resHeight={resHeight}
    >
      <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)}>
        <div />
      </LazyLoadWrapper>
      <HeroTextContainer marginTop={[0, 0]}>
        <StyledHeroTextColumn centered>
          <Columns.Column width={6}>
            <StyledTextPairing
              eyebrow={universalRegistrySectionData.eyeBrow}
              eyebrowColor="humanaDark4"
              eyebrowVariant="sectionEyeBrow20"
              eyebrowTagName="h3"
              title={universalRegistrySectionData.title}
              titleVariant="hed56"
              titleColor="humanaDark6"
              titleTagName="h3"
              body={universalRegistrySectionData.content}
              bodyVariant="dek20"
              bodyColor="neutra5"
              alignment={'center'}
            />
          </Columns.Column>
        </StyledHeroTextColumn>
        <Box style={{ textAlign: 'center' }} marginTop={[8, 8]} marginBottom={[5, 0]}>
          <GetStartedButton id="honeymoonUniversalRegistryGetStartedBtn" page={'registry'} variant="primary">
            {t('getStarted')}
          </GetStartedButton>
        </Box>
        <StateIconWrapper marginTop={[8]}>
          <StateIconTextContainer>
            <StateIcon>
              <Image src={GiftIcon} alt="GiftIcon" />
            </StateIcon>
            <IconTextPairing title={universalRegistrySectionData.registryTypeGifts} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIcon>
              <Image src={CashIcon} alt="CashIcon" />
            </StateIcon>
            <IconTextPairing title={universalRegistrySectionData.registryTypeCash} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIcon>
              <Image src={PlaneIcon} alt="PlaneIcon" />
            </StateIcon>
            <IconTextPairing title={universalRegistrySectionData.registryTypeExperiences} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIcon>
              <Image src={HeartIcon} alt="HeartIcon" />
            </StateIcon>
            <IconTextPairing title={universalRegistrySectionData.registryTypeCharity} {...smallTitleProps} />
          </StateIconTextContainer>
        </StateIconWrapper>
      </HeroTextContainer>
    </StyledUniversalRegistry>
  );
};
